import { ComponentPropsWithoutRef, forwardRef, useContext } from "react";
import cx from "classnames";

import { StepperContext } from "./Stepper";
import styles from "./StepperList.module.scss";

/**
 * Props for the StepperList component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type StepperListProps = ComponentPropsWithoutRef<"div">;

/**
 * Container component for organizing and displaying stepper steps.
 *
 * Features:
 * - Groups multiple stepper steps together
 * - Displays current step label
 * - Responsive design with mobile-friendly layout
 * - Accessible with proper ARIA roles
 * - Automatic step organization
 * - Visual step indicators
 * - Context-based current step display
 * - Flexible layout with CSS Grid/Flexbox
 * - Container queries for responsive behavior
 *
 * @example
 * <Stepper.List>
 *   <Stepper.Step controls="step1">Step 1</Stepper.Step>
 *   <Stepper.Step controls="step2">Step 2</Stepper.Step>
 *   <Stepper.Step controls="step3">Step 3</Stepper.Step>
 * </Stepper.List>
 */
export const StepperList = forwardRef<HTMLDivElement, StepperListProps>(
  (props, ref) => {
    const { className, children, onKeyDown, ...rest } = props;
    const { current, items } = useContext(StepperContext);

    const tabListClassNames = cx(styles["stepper-list"], className);
    const currentLabel =
      items?.[current].querySelectorAll("span")[1].textContent;

    return (
      <div
        {...rest}
        className={tabListClassNames}
        data-anv="stepper-list"
        ref={ref}
        role="tablist"
      >
        <div className={styles["stepper-steps"]}>{children}</div>
        <span className={styles.label}>{currentLabel}</span>
      </div>
    );
  },
);

StepperList.displayName = "StepperList";
